import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { faCopy, faDownload, faEdit, faPlus, faPlusCircle, faRetweet, faSave, faSearch, faShare, faShareAlt, faTable, faTags, faTimes, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { Accordion, Alert, Button, Col, Form, FormControl, FormLabel, FormSelect, Row} from "react-bootstrap";
import { ContentService } from "../../../services/ContentService";
import Loading from "../../components/common/Loading";
import ErrorMessage from "../../components/common/ErrorMessage";
import {
  IActionResult,
  IFooter,
  IHeader,
  ILanguage,
  IPage,
  ISiteDetails,
  IVersion
} from "../../../models/Interfaces";
import { VersionStatus } from "../../../config/Enums";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import { AddUpdatePageCommand } from "../../../models/content/AddUpdatePageCommand";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import { useConfirm } from "../../../config/ConfirmProvider";
import AppModal from "../../components/common/AppModal";
import ClonePageDialogue, { IClonePageRef } from "./ClonePageDialogue";
import { EventBus } from "../../../helpers/EventBus";
import { SiteService } from "../../../services/SiteService";
import ShareSocialMedia from "../socialmedia/ShareSocialMedia";
import { useNavigate } from "react-router-dom";
import styles from "./contentHome.module.scss";
import NewVersionWizard from "../../../AIHelper/NewVersionWizard";
import { IAIPageCreationBrief } from "../../../AIHelper/types";
import { AIContentGenerationService } from "../../../AIHelper/AIContentGenerationService";
import { isSiteAiConnected } from "../../../AIHelper/siteAi";
import VersionPreviewLink from "../../components/common/VersionPreviewLink";

import PageSeoPanel, { SeoSection } from "./PageSeoPanel";

interface IPageDetails {
  pageId: number;
  onEditStateChange?: (dirty: boolean, busy: boolean) => void;
  ActionCallback: (result: IActionResult) => void;
}

const PageDetails = (props: IPageDetails) => {
  const [activeTab, setActiveTab] = useState<"details" | SeoSection>("details");
  const [savedPage, setSavedPage] = useState<IPage | null>(null);
  const [pageSaving, setPageSaving] = useState(false);
  const [tabPrompt, setTabPrompt] = useState(false);
  const tabTransitionRef = useRef(false);
  const [seoState, setSeoState] = useState({ dirty: false, busy: false });
  const updateSeoState = useCallback((dirty: boolean, busy: boolean) => setSeoState({ dirty, busy }), []);
  const [page, setPage] = useState<IPage | null>(null);
  const [versions, setVersions] = useState<Array<IVersion>>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [pageLoadError, setPageLoadError] = useState<boolean>(false);
  const [headers, setHeaders] = useState<Array<IHeader>>([]);
  const [footers, setFooters] = useState<Array<IFooter>>([]);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [showClone, setShowClone] = useState<boolean>(false);
  const [clonePageVersion, setClonePageVersion] = useState<IVersion | null>(null);
  const [siteDetails, setSiteDetails] = useState<ISiteDetails | null>(null);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [showVersionWizard, setShowVersionWizard] = useState<boolean>(false);
  const [showGenerationOverlay, setShowGenerationOverlay] = useState<boolean>(false);
  const [generationMessage, setGenerationMessage] = useState<string>("Generating version...");

  const contentSvcRef = useRef<ContentService>();
  const siteSwitchRef = useRef<SiteSwitcher>();
  const siteSvcRef = useRef<SiteService>();
  const aiGenerationSvcRef = useRef<AIContentGenerationService>();

  const cloneFormRef = useRef<IClonePageRef>(null);

  const { requestConfirmation } = useConfirm();
  const navigate = useNavigate();

  const pageDirty = !!page && !!savedPage && JSON.stringify(page) !== JSON.stringify(savedPage);
  const dirty = pageDirty || seoState.dirty;
  const busy = pageSaving || seoState.busy || tabPrompt;
  useEffect(() => { props.onEditStateChange?.(dirty, busy); }, [dirty, busy, props.onEditStateChange]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty || busy) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, busy]);
  const changeTab = async (tab: "details" | SeoSection) => {
    if (tab === activeTab || busy || tabTransitionRef.current) return;
    tabTransitionRef.current = true;
    try {
      if (dirty) {
        setTabPrompt(true);
        if (!await requestConfirmation("Discard your unsaved changes before switching tabs?", "Discard changes")) return;
      }
      if (pageDirty && savedPage) setPage({ ...savedPage });
      setSeoState({ dirty: false, busy: false });
      setActiveTab(tab);
    } finally { tabTransitionRef.current = false; setTabPrompt(false); }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    if (name && page) {
      let val = value;
      if (name.toLowerCase() === "url") {
        val = value
          .replace(/\s+/g, '-') // Replace spaces with dashes
          .replace(/[^a-zA-Z0-9\-._~:\/?#[\]@!$&'()*+,;=%]/g, '');
      }

      setPage({
        ...page,
        [name]: val
      });
    }
  };

  const getSiteDetails = async (): Promise<void> => {
    if (siteSvcRef.current) {
      const res = await siteSvcRef.current.getSiteDetails();
      setSiteDetails(res);
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = event.target;
    if (name && page) {
      setPage({
        ...page,
        [name]: checked
      });
    }
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target;
    if (name && page) {
      setPage({
        ...page,
        [name]: value
      });
    }
  };

  const savePage = async (): Promise<void> => {
    if (!contentSvcRef.current || !page || pageSaving) return;
    const cmd = AddUpdatePageCommand.MapFromModel(page);
    if (!cmd) return;
    setPageSaving(true);
    try {
      const res = await contentSvcRef.current.addUpdatePage(cmd);
      actionEmitter.emit(EmitEvent.Result, res.Success, res.Message);
      if (res.Success) setSavedPage({ ...page });
    } catch {
      actionEmitter.emit(EmitEvent.Result, false, "Unable to save this page. Your changes have been kept. Please retry.");
    } finally { setPageSaving(false); }
  };

  const deletePage = async (pageid: number): Promise<void> => {
    if (contentSvcRef.current) {
      const delconfirm = await requestConfirmation("Delete this page?  All content and versions will be removed.<br /><b>This action cannot be undone.</b>", "Delete Page");

      if (delconfirm) {
        const res = await contentSvcRef.current.deletePage(pageid);

        if (res && props.ActionCallback) {
          props.ActionCallback(res);
        }
      }
    }
  };

  const addPageVersion = async (): Promise<IVersion | null> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.addVersion(props.pageId);

      if (res && res !== null && page) {
        setVersions(prev => [
          ...prev,
          res as IVersion
        ]);
        return res;
      }
    }
    return null;
  };

  const openVersionWizard = (): void => {
    if (!isSiteAiConnected(siteDetails)) {
      createManualVersion();
      return;
    }

    setShowVersionWizard(true);
  };

  const closeVersionWizard = (): void => {
    setShowVersionWizard(false);
  };

  const createManualVersion = async (): Promise<void> => {
    const res = await addPageVersion();
    if (res) {
      closeVersionWizard();
    }
  };

  const createAIVersion = async (brief: IAIPageCreationBrief): Promise<void> => {
    if (!page || !aiGenerationSvcRef.current) {
      return;
    }

    setShowGenerationOverlay(true);
    setGenerationMessage("Creating draft version");

    try {
      const result = await aiGenerationSvcRef.current.generateVersionFromBrief(page, brief, (message) => {
        setGenerationMessage(message);
      });

      if (result) {
        await getPage();
        closeVersionWizard();
        navigate(`/content/design/${page.SiteId}/${result.version.Id}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "AI generation failed.";
      actionEmitter.emit(EmitEvent.Result, false, message);
    } finally {
      setShowGenerationOverlay(false);
    }
  };

  const getLanguages = async (): Promise<void> => {
    const langs = await contentSvcRef.current?.getSiteLanguages();
    if (langs?.length) {
      setLanguages(langs);
    }
  };

  const getHeaders = async (): Promise<void> => {
    const headers = await contentSvcRef.current?.getSiteHeaders();

    if (headers?.length) {
      setHeaders(headers);
    }
  };

  const getFooters = async (): Promise<void> => {
    const footers = await contentSvcRef.current?.getSiteFooters();

    if (footers?.length) {
      setFooters(footers);
    }
  };

  const getPage = async (): Promise<void> => {
    const page = await contentSvcRef.current?.getPageDetails(Number(props.pageId));

    if (page) {
      setPage(page);
      setSavedPage({ ...page });
      setVersions(page.Versions ?? []);
      setPageLoading(false);
      setPageLoadError(false);
    } else {
      setPageLoadError(true);
      setPageLoading(false);
    }
  };

  const getVersionStatusLabel = (ver: IVersion): string => VersionStatus[ver.Status];

  const formatVersionDate = (value?: string | null): string => {
    if (!value) {
      return "Not recorded";
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Not recorded" : date.toDateString();
  };

  const versionDateText = (value?: string | null, person?: string | null): string => {
    const date = formatVersionDate(value);
    return person ? `${date} · ${person}` : date;
  };

  const getVersionClass = (status: number): string => {
    return status === 0 ? `${styles.versionCard} ${styles.versionCardDraft}` : `${styles.versionCard} ${styles.versionCardPublished}`;
  };

  const getVersionStatusClass = (status: number): string => {
    return status === VersionStatus.Published ? styles.versionStatusPublished : styles.versionStatusDraft;
  };

  const getSortedVersions = (): Array<IVersion> => {
    return [...versions].sort((a, b) => b.Version - a.Version);
  };

  const renderVersionCard = (ver: IVersion) => (
    <div className={getVersionClass(ver.Status)} key={`PV-${ver.Id}`}>
      <Row className="align-items-center g-1">
        <Col xs={12} lg={6}>
          <div className={styles.versionSummary}>
            <div className={styles.versionHeadline}>
              <span className={styles.versionNumber}>V-{ver.Version}</span>
              <span className={`${styles.versionStatus} ${getVersionStatusClass(ver.Status)}`}>{getVersionStatusLabel(ver)}</span>
            </div>
            <dl className={styles.versionDates}>
              <div>
                <dt>Created</dt>
                <dd>{versionDateText(ver.DateCreated, ver.CreatedBy)}</dd>
              </div>
              <div>
                <dt>Edited</dt>
                <dd>{versionDateText(ver.DateModified, ver.ModifiedBy)}</dd>
              </div>
            </dl>
            <p className={styles.versionDateNote}>Publishing or unpublishing updates the edited time. A separate published time is not stored.</p>
          </div>
        </Col>
        <Col xs={12} lg={6}>
          <div className={styles.versionActions}>
          {hasSocialMedia() && ver.Status === VersionStatus.Published && (
            <Button
              variant="outline-secondary"
              title="share to social media"
              className={`btn-nomin me-1 ${styles.actionButton} ${styles.versionIconAction}`}
              onClick={() => setShowShare(true)}>
              <FontAwesomeIcon icon={faShareAlt} />
            </Button>
          )}

          <Button
            variant="outline-secondary"
            className={`btn-nomin me-1 ${styles.actionButton} ${styles.versionIconAction}`}
            title="edit version"
            onClick={() => editVersion(ver.Id)}>
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <VersionPreviewLink
            previewUrl={ver.PreviewUrl ?? ""}
            domain={siteDetails?.Domain ?? ver.Domain}
            rel="noopener noreferrer"
            target="_blank"
            className={`btn btn-nomin btn-outline-secondary me-1 ${styles.actionButton} ${styles.versionIconAction}`}
            title="preview">
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </VersionPreviewLink>
          <Button
            variant="outline-secondary"
            title="clone version"
            onClick={() => cloneVersion(ver)}
            className={`btn-nomin me-1 ${styles.actionButton} ${styles.versionIconAction}`}>
            <FontAwesomeIcon icon={faCopy}></FontAwesomeIcon>
          </Button>
          <Button
            variant="outline-secondary"
            title="publish version"
            onClick={() => publishVersion(ver.Id)}
            className={`btn-nomin me-1 ${styles.actionButton} ${styles.versionIconAction}`}
            disabled={ver.Status === VersionStatus.Published}>
            <FontAwesomeIcon icon={faUpload}></FontAwesomeIcon>
          </Button>
          <Button
            variant="outline-secondary"
            title="un-publish version"
            onClick={() => unPublishVersion(ver.Id)}
            className={`btn-nomin me-1 ${styles.actionButton} ${styles.versionIconAction}`}
            disabled={ver.Status === VersionStatus.Draft}>
            <FontAwesomeIcon icon={faDownload}></FontAwesomeIcon>
          </Button>
          <Button
            variant="outline-secondary"
            title="delete version"
            onClick={() => deleteVersion(ver.Id)}
            className={`btn-nomin me-1 ${styles.actionButton} ${styles.versionIconAction}`}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </Button>
          </div>
        </Col>
      </Row>
    </div>
  );

  const editVersion = (verid: number): void => {
    if (siteSwitchRef.current) {
      const siteid = siteSwitchRef.current.getSelectedSiteKey();

      if (siteid) {
        const url = `/content/design/${siteid}/${verid}`;
        window.open(url, "_blank")
      }
    }
  };

  const cloneVersion = (version: IVersion): void => {
    if (version) {
      setClonePageVersion(version);
      setShowClone(true);
    }
  };

  const publishVersion = async (verid: number): Promise<void> => {
    if (contentSvcRef.current) {
      const versionres = await contentSvcRef.current.publishVersion(verid);

      if (versionres && page) {
        setVersions(prev => prev.map(ver => {
          if (ver.Id === versionres.Id) {
            return versionres;
          }

          if (ver.Status === VersionStatus.Published) {
            return {
              ...ver,
              Status: VersionStatus.Draft,
              StatusDescription: "Draft"
            };
          }

          return ver;
        }));
      }
    }
  };

  const unPublishVersion = async (verid: number): Promise<void> => {
    if (contentSvcRef.current) {
      const versionres = await contentSvcRef.current.unPublishVersion(verid);

      if (versionres && page) {
        setVersions(prev => prev.map(ver => ver.Id === versionres.Id ? versionres : ver));
      }
    }
  };

  const closeDeletedClone = useCloseDeletedSelection(clonePageVersion, (version) => version.Id, () => closeClone());

  const deleteVersion = async (verid: number): Promise<void> => {
    if (contentSvcRef.current) {

      const confirmed = await requestConfirmation("Are you sure you want to remove this version?");

      if (confirmed) {
        const res = await contentSvcRef.current.deleteVersion(verid);

        if (res) {
          closeDeletedClone(verid);
          if (versions.length > 0) {
            setVersions(prev => prev.filter(v => v.Id !== verid));
          }
        }
      }
    }
  };

  const closeClone = (): void => {
    setClonePageVersion(null);
    setShowClone(false);
  };

  const cloneNewPage = async (page: AddUpdatePageCommand): Promise<void> => {
    if (cloneFormRef.current && page && contentSvcRef.current && clonePageVersion) {
      const page = await cloneFormRef.current.getPage();

      if (page !== null) {
        const cloneres = await contentSvcRef.current.cloneVersionNewPage(clonePageVersion.Id, page);

        if (cloneres) {
          actionEmitter.emit(EmitEvent.Result, cloneres.Success, cloneres.Message ?? "Page created from version");
          if (props.ActionCallback) {
            props.ActionCallback(cloneres);
          }
        } else {
          actionEmitter.emit(EmitEvent.Result, false, "Failed to create page from version");
        }
        closeClone();
      }
    }
  };

  const cloneSamePage = async (versionid: number): Promise<void> => {
    if (contentSvcRef.current && versionid) {
      const res = await contentSvcRef.current.cloneVersionSamePage(versionid);
      if (res) {
        setVersions(prev => [
          ...prev,
          res
        ]);
        closeClone();
      }
    }
  };

  const hasSocialMedia = (): boolean => {
    let smset: boolean = false;

    smset = (siteDetails?.ShareFacebook ?? false) ||
      (siteDetails?.ShareGoogle ?? false) ||
      (siteDetails?.ShareInstagram ?? false) ||
      (siteDetails?.ShareX ?? false);

    return smset;
  };

  useEffect(() => {
    EventBus.getInstance().subscribe(EventBus.Event.OnAddPageVersion, openVersionWizard);

    return () => {
      EventBus.getInstance().unsubscribe(EventBus.Event.OnAddPageVersion, openVersionWizard);
    }
  }, []);

  useEffect(() => {
    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!siteSwitchRef.current) {
      siteSwitchRef.current = new SiteSwitcher();
    }

    if (!siteSvcRef.current) {
      siteSvcRef.current = new SiteService();
    }

    if (!aiGenerationSvcRef.current) {
      aiGenerationSvcRef.current = new AIContentGenerationService();
    }

    const getData = async () => {
      try {
        await Promise.all([
          getLanguages(),
          getHeaders(),
          getFooters(),
          getSiteDetails(),
          getPage()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setPageLoading(false);
      }
    }

    getData();

  }, [props.pageId]);

  const sortedVersions = getSortedVersions();
  const publishedVersions = sortedVersions.filter(ver => ver.Status === VersionStatus.Published);
  const draftVersions = sortedVersions.filter(ver => ver.Status === VersionStatus.Draft);

  return (
    <>
      {!pageLoading && !pageLoadError && page && (
        <>
          <div className={styles.detailStack}>
          <div className={styles.detailPanel}>
            <div className={styles.panelHeader}>
              <div className={styles.panelTitleWrap}>
                <span className={styles.panelTitle}>Page Details</span>
                <span className={styles.panelSubtle}>{savedPage?.Title ?? page.Title} · {savedPage?.Url ?? page.Url}</span>
              </div>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.pageToolbar}>
              <div role="tablist" aria-label="Page settings" className={styles.pageTabs}>
                {([["details", "Page details"], ["meta", "Meta tags"], ["keywords", "Keywords"], ["redirects", "Redirects"]] as const).map(([tab, label]) =>
                  <Button key={tab} id={`page-tab-${tab}`} role="tab" aria-selected={activeTab === tab} aria-controls="page-settings-panel" tabIndex={activeTab === tab ? 0 : -1} onKeyDown={event => {
                    const tabs = ["details", "meta", "keywords", "redirects"] as const;
                    const index = tabs.indexOf(tab);
                    const next = event.key === "ArrowRight" ? tabs[(index + 1) % tabs.length] : event.key === "ArrowLeft" ? tabs[(index + tabs.length - 1) % tabs.length] : event.key === "Home" ? tabs[0] : event.key === "End" ? tabs[3] : null;
                    if (next) { event.preventDefault(); document.getElementById(`page-tab-${next}`)?.focus(); void changeTab(next); }
                  }} variant={activeTab === tab ? "primary" : "outline-secondary"} disabled={busy} onClick={() => void changeTab(tab)}>{label}</Button>)}
              </div>
                  <div className={styles.actionBar}>
                  <Button type="button" variant="primary" className={`btn-nomin me-1 ${styles.actionButton}`} title="add new version" disabled={busy} onClick={openVersionWizard}>
                    <FontAwesomeIcon icon={faPlusCircle} />
                    <span>Add version</span>
                  </Button>
                  <Button type="button" variant="danger" className={`btn-nomin me-1 ${styles.actionButton}`} disabled={busy} onClick={() => deletePage(props.pageId)} title="delete page">
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Delete page</span>
                  </Button>
                  </div>
              </div>
              <div id="page-settings-panel" role="tabpanel" aria-labelledby={`page-tab-${activeTab}`}>
              {activeTab !== "details" && <PageSeoPanel key={`${page.Id}-${activeTab}`} page={savedPage ?? page} section={activeTab} onStateChange={updateSeoState} />}
              {activeTab === "details" && <fieldset disabled={pageSaving} className={styles.pageFields}>
              <Button type="button" variant="success" className="mb-3" onClick={() => void savePage()}>{pageSaving ? "Saving…" : "Save page"}</Button>
              <Row>
                <Col xs={12} md={8} className="mt-sm-3 mt-md-0">
                  <FormLabel>Url</FormLabel>
                  <FormControl
                    type="text"
                    name="Url"
                    placeholder="url"
                    value={page.Url}
                    onChange={handleInputChange} />
                </Col>
                <Col xs={12} md={4} className="mt-sm-3 mt-md-0">
                  <FormLabel>Language</FormLabel>
                  <FormSelect
                    title="select language"
                    value={page?.Language ?? ""}
                    name="Language"
                    onChange={handleSelectChange}>
                    <option value="">Set page language...</option>
                    {languages?.map(lng =>
                      <option key={`lng-${lng.Id}`} value={lng.ShortCode}>{lng.Language}</option>
                    )}
                  </FormSelect>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={4} className="mt-3">
                  <FormLabel>Title</FormLabel>
                  <FormControl
                    type="text"
                    name="Title"
                    placeholder="title"
                    value={page.Title}
                    onChange={handleInputChange} />
                </Col>
                <Col xs={12} md={4} className="mt-3">
                  <FormLabel>Alias</FormLabel>
                  <FormControl
                    type="text"
                    name="Alias"
                    placeholder="alias"
                    value={page.Alias}
                    onChange={handleInputChange} />
                </Col>
                <Col xs={12} md={2} className="mt-3">
                  <FormLabel htmlFor="excludeSiteMap">Sitemap Exclude</FormLabel>
                  <input
                    title="Exclude from sitemap"
                    type="checkbox"
                    name="ExcludeSiteMap"
                    id="excludeSiteMap"
                    className="form-check btn-nomin"
                    checked={page?.ExcludeSiteMap}
                    onChange={handleCheckChange} />
                </Col>
                <Col xs={12} md={2} className="mt-3">
                  <FormLabel htmlFor="newsPage">News Page</FormLabel>
                  <input
                    title="Page is a news page"
                    type="checkbox"
                    name="IsNewsPage"
                    id="newsPage"
                    className="form-check btn-nomin"
                    checked={page?.IsNewsPage}
                    onChange={handleCheckChange} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={4} className="mt-3">
                  <FormLabel>Header</FormLabel>
                  <FormSelect
                    title="SelectHeader"
                    name="HeaderId"
                    className="form-select"
                    value={page.HeaderId ?? ""}
                    onChange={handleSelectChange}>
                    <option value="">Select page header...</option>
                    {headers?.map(hdr =>
                      <option key={`hdr-${hdr.Id}`} value={hdr.Id}>{hdr.Name}</option>
                    )}
                  </FormSelect>
                </Col>
                <Col xs={12} md={4} className="mt-3">
                  <FormLabel>Footer</FormLabel>
                  <FormSelect
                    title="SelectFooter"
                    className="form-select"
                    name="FooterId"
                    value={page?.FooterId ?? ""}
                    onChange={handleSelectChange}>
                    <option value="">Select page footer...</option>
                    {footers?.map(ftr =>
                      <option key={`ftr-${ftr.Id}`} value={ftr.Id}>{ftr.Title}</option>
                    )}
                  </FormSelect>
                </Col>
              </Row>
              </fieldset>}
              </div>
            </div>
          </div>

          {activeTab === "details" && <div data-bind="visible: $root.SelectedPage, with: $root.SelectedPage" className={styles.detailPanel}>
            <div className={styles.panelHeader}>
              <div className={styles.panelTitleWrap}>
                <span className={styles.panelTitle}>Content Versions</span>
                <span className={styles.panelSubtle}>Draft, preview, publish, clone, and manage the versions behind this page.</span>
              </div>
            </div>
            <div className={styles.panelBody}>
            {versions.length > 0 && (
              <div className={styles.versionList}>
                {publishedVersions.length > 0 && (
                  <div className={styles.versionGroup}>
                    <div className={styles.versionGroupLabel}>Published</div>
                    <div className={styles.versionGroupList}>
                      {publishedVersions.map(renderVersionCard)}
                    </div>
                  </div>
                )}

                {draftVersions.length > 0 && (
                  <Accordion defaultActiveKey={draftVersions.length <= 3 ? "drafts" : undefined} className={styles.versionAccordion}>
                    <Accordion.Item eventKey="drafts" className={styles.versionAccordionItem}>
                      <Accordion.Header className={styles.versionAccordionHeader}>
                        <span className={styles.versionGroupLabel}>Drafts ({draftVersions.length})</span>
                      </Accordion.Header>
                      <Accordion.Body className={styles.versionAccordionBody}>
                        <div className={styles.versionGroupList}>
                          {draftVersions.map(renderVersionCard)}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}
              </div>
            )}

            {!(versions?.length > 0) && (
              <Row className="mb-1">
                <Col xs={12} className="text-center">
                  <Alert variant="light" className="m-0 p-4">
                    <span className="bold">No versions found</span>
                    <br />
                    <Button variant="outline-secondary"
                      type="button"
                      className={`btn-nomin mt-3 ${styles.actionButton}`}
                      title="add new version"
                      onClick={openVersionWizard}>
                      <FontAwesomeIcon icon={faPlus} className="me-1"></FontAwesomeIcon>
                      <span>add new version</span>
                    </Button>
                  </Alert>
                </Col>
              </Row>
            )}
            </div>
          </div>}
          </div>
        </>
      )}

      {showClone && languages && languages.length > 0 && clonePageVersion && (
        <AppModal
          onClose={closeClone}
          showButtons={false}
          size="lg"
          title="Clone Version">
          <ClonePageDialogue
            Languages={languages}
            SourceVersion={clonePageVersion}
            SourcePageLanguage={page?.Language ?? null}
            SourcePageAlias={page?.Alias ?? null}
            samePageCallback={cloneSamePage}
            newPageCallback={cloneNewPage}
            cancelCallback={closeClone}
            ref={cloneFormRef}
          />
        </AppModal>
      )}

      {showVersionWizard && page && (
        <AppModal
          onClose={closeVersionWizard}
          showButtons={false}
          size="lg"
          title="Create Version">
          <NewVersionWizard
            pageDetails={AddUpdatePageCommand.MapFromModel(page) as AddUpdatePageCommand}
            aiAvailable={isSiteAiConnected(siteDetails)}
            onCancel={closeVersionWizard}
            onManualVersionCreated={createManualVersion}
            onAIVersionCreated={createAIVersion}
          />
        </AppModal>
      )}

      {showGenerationOverlay && (
        <AppModal
          onClose={() => undefined}
          showButtons={false}
          size="md"
          title="Generating Draft Version"
          backdrop="static">
          <Loading SpinSize="3x" Message={generationMessage} />
        </AppModal>
      )}

      {pageLoading && !pageLoadError && (
        <Loading SpinSize="5x" Message="Loading page details..." />
      )}

      {pageLoadError && (
        <ErrorMessage Message="Failed to load page details" ShowIcon={true} />
      )}

      {showShare && (
        <ShareSocialMedia
          pageurl={page?.Url ?? "/"}
          sitedetails={siteDetails}
          closeCallback={() => setShowShare(false)}
        />
      )}

    </>
  );
}

export default PageDetails;
